import { createMemo, Match, Switch, type Accessor } from 'solid-js';
import { DrawResizeHandles } from 'Shared/posters/canvas/DrawResizeHandles';
import { ResizeMode } from 'Shared/posters/canvas/interactions/ResizeMode';
import { detachIfLinked } from 'Shared/posters/canvas/interactions/linkedLeaf';
import type { DesignLinkContext } from 'Shared/posters/project/designLink';
import type { Mode } from 'Shared/posters/project/PosterMode';
import type { ITextImpl } from 'Shared/posters/canvas/text/AbstractTextImpl';
import { useUser } from 'Shared/SiteProvider';
import type { RuntimeContainer } from 'Shared/posters/types';
import { Canvas } from 'Shared/posters/project/posterSchema';
import { DragContext } from 'Shared/posters/canvas/drag';
import type { ObjectContext } from 'Shared/posters/project/objects';
import { HistoryContext } from 'Shared/posters/project/history';
import { ImplRegistry } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { Box } from 'Shared/posters/types';


export function TextResizeHandles(P)
{
	// effectiveBox/getBBox take the same `!useUser()` argument SelectableObject passes when it draws
	// the selection rect, so the handles sit on the identical (possibly clamped) box. Read as an
	// accessor by the starters below, never eagerly: each detaches a linked leaf first, and the seed
	// has to be the box that write leaves behind (see detachText).
	const box = createMemo(() => P.impl.effectiveBox(!useUser()));

	return (
		<Switch>
			<Match when={P.impl.resizeHandles() == 'fieldBox'}>
				<DrawResizeHandles impl={P.impl}
					positions={['e', 'w']}
					onHandleDown={(handle,e) =>
						fieldWidthResize(e,P.impl,handle,P.canvas,P.drag,P.objects,P.selection.impls,P.design,P.mode,box,P.resizeMode)
					}
				/>
			</Match>
			<Match when={P.impl.resizeHandles() == 'textCorner'}>
				<DrawResizeHandles impl={P.impl} positions={['nw', 'ne', 'se', 'sw']}
					onHandleDown={(handle,e) =>
						textCornerResize(e,P.impl,P.canvas,handle,P.drag,P.objects,P.selection.impls,P.history,P.design,P.mode,P.impl.contentOffsetY(),P.impl.setContentOffsetY,box,P.resizeMode)
					}
				/>
				<DrawResizeHandles impl={P.impl} positions={['e', 'w']}
					onHandleDown={(handle,e) =>
						textWidthResize(e,P.impl,handle,P.canvas,P.drag,P.objects,P.design,P.mode,box,P.resizeMode)
					}
				/>
			</Match>
		</Switch>
	);
}

// The shared opening of every text resize: detach a linked leaf so the template stops owning its
// position (until it does, a west/north handle can only change the size and the box grows from the
// opposite edge — see detachIfLinked), record the gesture, and hand back the box *after* that write.
// The slot rides on the ResizeMode until SelectableObject's mouseup commits the gesture.
function detachText(impl:ITextImpl,design:DesignLinkContext,objCtx:ObjectContext,mode:Mode,box:Accessor<Box>,resizeMode:ResizeMode,textMode:'textWidth'|'fieldWidth'|'textCorner'): Box
{
	const detached = detachIfLinked(impl,design,objCtx,mode);
	resizeMode.begin(textMode, detached?.slot ?? null);
	const seed = box();
	console.log('[RESIZE-DETACH]', textMode, impl.id, 'detached=', !!detached, 'seed=', JSON.stringify(seed.pos), 'live=', JSON.stringify(impl.box.pos));
	return seed;
}

function textWidthResize(e:MouseEvent,impl:ITextImpl,handle:string,canvas:Canvas,dragCtx:DragContext,objCtx:ObjectContext,design:DesignLinkContext,mode:Mode,box:Accessor<Box>,resizeMode:ResizeMode)
{
	e.preventDefault();
	e.stopPropagation();
	const effectiveBox = detachText(impl,design,objCtx,mode,box,resizeMode,'textWidth');
	const storeBox     = impl.box;
	// Seed from the live (possibly hugged) size, matching the drawn handles, so the box doesn't jump
	// to a stale stored width on the first drag frame. getSize() is storeBox.dims.width for everything but an
	// un-resized user-mode field.
	const w0 = impl.getSize().width;
	const l0 = effectiveBox.pos.x;

	dragCtx.beginResize({ x: e.clientX, y: e.clientY }, (dx, _dy) => {
		const rawW    = handle === 'e' ? w0 + dx : w0 - dx
		const maxW    = handle === 'e' ? (canvas.width ?? Infinity) - l0 : l0 + w0
		const newW    = Math.max(30, Math.min(rawW, maxW))
		const newLeft = handle === 'w' ? l0 + w0 - newW : l0
		const newH    = impl.getWrappedHeight(newW) ?? (storeBox.dims.height ?? 60)
		impl.onWidthResize(newW)
		impl.writeGeometry({ pos: { x: newLeft, y: storeBox.pos.y }, dims: { width: newW, height: newH } })
	}, () => {});
}

function fieldWidthResize(e:MouseEvent,impl:ITextImpl,handle:string,canvas:Canvas,dragCtx:DragContext,objCtx:ObjectContext,impls:ImplRegistry,design:DesignLinkContext,mode:Mode,box:Accessor<Box>,resizeMode:ResizeMode)
{
	e.preventDefault();
	e.stopPropagation();
	const effectiveBox = detachText(impl,design,objCtx,mode,box,resizeMode,'fieldWidth');
	const storeBox     = impl.box;
	const w0 = storeBox.dims.width ?? 100;
	const l0 = effectiveBox.pos.x;
	const t0 = effectiveBox.pos.y;
	const h0 = storeBox.dims.height ?? 60;
	const align = impl.verticalAlign();
	// A flex-container child is re-anchored from its width by the container every frame (see
	// ContainerImpl.alignLeft), so the dragged edge no longer tracks the cursor 1:1 — a centred child
	// grows symmetrically (each side takes half the change) so its edge crawls at half-speed, needing
	// several drags to reach the poster edge. cross carries the container's cross-axis align + width so
	// the drag can map cursor movement to the width that lands the dragged edge under the cursor. null →
	// absolute layout, where the opposite edge is pinned and the dragged edge absorbs the whole change.
	const cross = crossAxisLayout(impl, objCtx, impls, canvas.width);
	const rate  = edgeRate(handle, cross);

	dragCtx.beginResize({ x: e.clientX, y: e.clientY }, (dx, _dy) => {
		// dx is the cursor's movement; dividing by the edge rate gives the width change that moves the
		// dragged edge that far (rate 0 → a container-pinned edge, so its handle can't resize).
		const rawW    = rate === 0 ? w0 : w0 + dx / rate
		const maxW    = cross ? cross.width : (handle === 'e' ? (canvas.width ?? Infinity) - l0 : l0 + w0)
		const newW    = Math.max(30, Math.min(rawW, maxW))
		// Re-wrap at the new width and keep the bounded (aligned) edge fixed as the height changes.
		const newH    = impl.getWrappedHeight(newW) ?? h0
		impl.onFieldWidthResize(newW)
		if (cross) {
			// The container owns this child's position (layoutPos); only its size feeds back into the
			// layout, so write width/height and let the container re-place it.
			impl.writeDims({ width: newW, height: newH })
		} else {
			const newLeft = handle === 'w' ? l0 + w0 - newW : l0
			const newTop  = align === 'center' ? t0 + (h0 - newH) / 2 : align === 'bottom' ? t0 + h0 - newH : t0
			impl.writeGeometry({ pos: { x: newLeft, y: newTop }, dims: { width: newW, height: newH } })
		}
	}, () => {});
}

// The cross-axis (horizontal) layout of a field that is a flex-container child: its parent container's
// align and content width. Returns null when the object is absolutely positioned (no parent, or an
// 'absolute'-layout parent) — there the dragged edge is simply pinned to the stored opposite edge.
function crossAxisLayout(impl,objCtx:ObjectContext,impls:ImplRegistry,canvasWidth?:number): { align: 'left'|'center'|'right'; width: number } | null
{
	const parentId = objCtx.findParentOf(impl.id);
	if (!parentId) return null;
	const parent = objCtx.getObject(parentId) as RuntimeContainer | null;
	if (!parent || (parent.layout ?? 'absolute') === 'absolute') return null;
	const width = impls[parentId]?.getSize?.()?.width ?? parent.size?.width ?? canvasWidth ?? Infinity;
	return { align: parent.align ?? 'left', width };
}

// How far the dragged edge moves per unit of width change, given how the box is anchored. Absolute
// layout pins the opposite edge, so the dragged edge absorbs the whole change (±1). A flex container
// re-anchors by its cross-axis align: 'center' splits the change across both edges (±0.5), while an
// edge the container pins ('right'-aligned box's right edge, 'left'-aligned box's left edge) doesn't
// move at all (0) — that side's handle can't resize.
function edgeRate(handle:string, cross:{ align:'left'|'center'|'right' } | null): number
{
	if (!cross) return handle === 'e' ? 1 : -1;
	if (cross.align === 'center') return handle === 'e' ? 0.5 : -0.5;
	if (cross.align === 'right')  return handle === 'e' ? 0 : -1;
	return handle === 'e' ? 1 : 0;   // left align
}

function textCornerResize(e: MouseEvent,impl:ITextImpl,canvas:Canvas,handle: string,dragCtx:DragContext,objCtx:ObjectContext,impls:ImplRegistry,histCtx:HistoryContext,design:DesignLinkContext,mode:Mode,contentOffsetY,setContentOffsetY,box:Accessor<Box>,resizeMode:ResizeMode)
{
	e.preventDefault();
	e.stopPropagation();
	// Seed from the live (possibly hugged) size, matching the drawn handles, so the aspect ratio and
	// box don't jump to a stale stored size on the first drag frame. getSize() is pos for everything
	// but an un-resized user-mode field. Read ahead of the detach, which starts the gesture: an
	// unmeasured box bails out below, and a gesture that never runs must not take a detachment.
	const sz = impl.getSize();
	const w0 = sz.width;
	const h0 = sz.height;
	if (w0 <= 0 || h0 <= 0)
		return;
	const effectiveBox = detachText(impl,design,objCtx,mode,box,resizeMode,'textCorner');
	const r = w0 / h0;
	const l0 = effectiveBox.pos.x;
	const t0 = effectiveBox.pos.y;
	const f0 = impl.startFontSize() ?? 24;
	const coy0 = contentOffsetY;

	dragCtx.beginResize({ x: e.clientX, y: e.clientY }, (dx, _dy) => {
		const rawW        = handle.includes('e') ? w0 + dx : w0 - dx;
		const maxW        = handle.includes('e') ? (canvas.width ?? Infinity) - l0 : l0 + w0;
		// Vertical clamp: cap the width (and thus the aspect-locked height) so the *glyphs* stay on
		// canvas. A north handle grows upward until the text top reaches 0; a south handle grows down
		// until the text bottom reaches the canvas height. The box origin sits coy0 above the ink (coy0
		// is negative), so the span to fit is h0 either way — the offset belongs in the distance to the
		// edge (vNum), not in the height being scaled. Dividing by h0∓coy0 instead clamped the box, which
		// hangs |coy0| below the ink: a south drag jammed with a font's worth of empty canvas below the
		// text, a north one let the text run off the top.
		const ch          = canvas.height ?? Infinity;
		const vNum        = handle.includes('n') ? t0 + coy0 + h0 : ch - t0 - coy0;
		const maxWV       = w0 * (vNum / h0);
		// An object already off canvas has a negative vNum, so the caps go below w0: floor them there so
		// the drag simply can't grow it, rather than collapsing it to the minimum on the first frame.
		const cap         = Math.max(w0, Math.min(maxW, maxWV));
		const newW        = Math.max(10, Math.min(rawW, cap));
		const newH        = newW / r;
		const scale       = newW / w0;
		const newFontSize = Math.round(f0 * scale * 10) / 10;
		const newLeft     = handle.includes('w') ? l0 + w0 - newW : l0;
		const topAdjust   = coy0 * (1 - scale);
		const newTop      = handle.includes('n') ? t0 + topAdjust + h0 - newH : t0 + topAdjust;
		impl.writeGeometry({ pos: { x: newLeft, y: newTop }, dims: { width: newW, height: newH } });
		setContentOffsetY(coy0 * scale);
		impl.onCornerResize(newFontSize,objCtx,impls,histCtx);
	}, () => {})
}

